import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import SubHeader from "../customComponents/SubHeader";
import api from "../../utils/axiosInstance";
import { showAlert } from "../../redux/alertSlice";
import {
  BanknotesIcon,
  SparklesIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/solid";

const Dashboard = ({ toggleMobileMenu }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState({ wmlgrate: "", wmlgsrate: "" });

  const fetchPrices = async () => {
    try {
      setLoading(true);
      const res = await api.get("/store/get-prices");
      if (res?.data?.success) {
        setPrices(res.data.data || { wmlgrate: "", wmlgsrate: "" });
      } else {
        dispatch(
          showAlert({ type: "warning", message: "Failed to fetch store prices." })
        );
      }
    } catch (err) {
      console.error("Error fetching prices", err);
      dispatch(
        showAlert({ type: "error", message: "Error while fetching store prices." })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inr = useMemo(() =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 })
  , []);

  const parseToNumber = (val) => {
    if (val === null || val === undefined) return null;
    const cleaned = String(val).replace(/[^0-9.-]/g, "");
    const num = Number.parseFloat(cleaned);
    return Number.isNaN(num) ? null : num;
  };

  const goldRate = parseToNumber(prices?.wmlgrate);
  const silverRate = parseToNumber(prices?.wmlgsrate);

  const PriceCard = ({ title, value, icon: Icon, accentFrom, accentTo }) => (
    <div className="relative overflow-hidden rounded-2xl bg-white shadow-custom-dark ring-1 ring-black/5">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentFrom} ${accentTo}`} />
      <div className="p-5 sm:p-6 flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <Icon className="h-6 w-6 text-primary" />
            <p className="text-sm font-mont font-semibold text-primary/80">{title}</p>
          </div>
          <p className="mt-3 text-2xl sm:text-3xl font-bold text-primary">
            {value ?? "—"}
          </p>
        </div>
        <div className="ml-4">
          <div className="h-12 w-12 rounded-xl bg-fourth flex items-center justify-center">
            <CurrencyRupeeIcon className="h-6 w-6 text-third" />
          </div>
        </div>
      </div>
    </div>
  );

  const QuickLink = ({ to, icon: Icon, label, description }) => (
    <Link
      to={to}
      className="group rounded-2xl bg-white/90 backdrop-blur-sm ring-1 ring-black/5 shadow-custom-light hover:shadow-custom-medium transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-third/30"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-third/90 to-sixth/80 text-white flex items-center justify-center shadow-custom-medium">
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <p className="text-base font-mont font-semibold text-primary">{label}</p>
            {description && (
              <p className="text-xs text-primary/60 mt-0.5">{description}</p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="mx-auto w-full">
      <SubHeader text={"Dashboard"} toggleMobileMenu={toggleMobileMenu} />

      {/* Hero / Brand strip */}
      <div className="mx-4 md:mx-6 lg:mx-8">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary via-[#1b2130] to-sixth text-white shadow-custom-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent" />
          <div className="relative px-6 py-8 sm:px-8 sm:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-bask text-2xl sm:text-3xl tracking-wide">Jaina Jewellers ERP</h1>
              <p className="mt-1 text-sm sm:text-base font-mont text-white/80">
                Premium dashboard for daily operations — schemes, customers, pricing and more.
              </p>
            </div>
            <button
              onClick={fetchPrices}
              disabled={loading}
              className="mt-4 sm:mt-0 inline-flex items-center space-x-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-mont shadow-inner hover:bg-white/15 active:scale-[0.99] disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh Prices</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPIs / Prices */}
      <div className="mx-4 md:mx-6 lg:mx-8 mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <PriceCard
          title="Gold Rate"
          value={goldRate !== null ? inr.format(goldRate) + " / 10g" : prices?.wmlgrate || "—"}
          icon={BanknotesIcon}
          accentFrom="from-[#f3c14e]"
          accentTo="to-[#b88a2b]"
        />
        <PriceCard
          title="Silver Rate"
          value={silverRate !== null ? inr.format(silverRate) + " / kg" : prices?.wmlgsrate || "—"}
          icon={SparklesIcon}
          accentFrom="from-[#c0c7d1]"
          accentTo="to-[#8b98a7]"
        />
      </div>

      {/* Quick Actions */}
      <div className="mx-4 md:mx-6 lg:mx-8 mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg sm:text-xl font-mont font-semibold text-primary">Quick actions</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <QuickLink to="/home/store-prices" icon={Cog6ToothIcon} label="Store Prices" description="Update gold & silver rates" />
          <QuickLink to="/home/customer-profile" icon={UserGroupIcon} label="Customer Profiles" description="Find and manage customers" />
          <QuickLink to="/home/scheme-generation" icon={ShieldCheckIcon} label="Scheme Generation" description="Create and manage schemes" />
          <QuickLink to="/home/statement" icon={DocumentTextIcon} label="Statements" description="View account statements" />
          <QuickLink to="/home/scheme-summary" icon={ChartBarIcon} label="Scheme Summary" description="Overview & history" />
          <QuickLink to="/home/user" icon={UserGroupIcon} label="Users" description="Manage application users" />
          <QuickLink to="/home/loyalty-points" icon={SparklesIcon} label="Loyalty Points" description="Configure rewards" />
          <QuickLink to="/home/message-configuration" icon={DocumentTextIcon} label="Message Config" description="SMS & notifications" />
          <QuickLink to="/home/mobile-app" icon={DevicePhoneMobileIcon} label="Mobile App" description="App content & settings" />
        </div>
      </div>

      {/* Info / Announcements */}
      <div className="mx-4 md:mx-6 lg:mx-8 my-8">
        <div className="rounded-2xl bg-fourth p-5 sm:p-6 ring-1 ring-black/5 shadow-custom-light">
          <h3 className="text-base font-mont font-semibold text-primary">Notes</h3>
          <p className="mt-1 text-sm text-primary/70">
            Keep your store prices updated for accurate billing across modules. You can refresh rates
            from the top-right button or manage manually in Store Prices.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
